import styled, {css} from 'styled-components';
import React, {FC} from 'react';

import {fontSizesMixinsR4, fontSizesMixinsMobileR4, paletteR4} from '../../../../lib/styleVariablesR4';
import {
  ImageTextBlock,
  ImageTextBlockProps,
  StyledDescription as ImageTextBlockDescription,
  StyledTitle as ImageTextBlockTitle
} from '../../../molecules/textImageBlockR4';
import {StylableProps} from '../../../../lib/styleHelpers';
import {mediaBreakpointDown} from '../../../../lib/styleVariables';
import {hashObject} from '../../../../lib/hashObject';
import {Slider} from '../../../common/slider/basic';
import {hiddenMobileDownStyle, hiddenTabletUpStyle} from '../../../../lib/responsiveClassNames';

export interface DynamicImageTextXSCenteredR4Props {
  imgSize?: 'regular' | 'mini';
  itemsPerRow?: number;
  withSliderOnMobile?: boolean;
}

export type Size = 'small' | 'large';
type ItemType = Omit<ImageTextBlockProps, 'size' | 'itemPosition'>;
interface ImageTextXSCenteredProps extends DynamicImageTextXSCenteredR4Props, StylableProps {
  items: ItemType[];
  title?: string;
  description?: string;
  size: Size;
  itemPosition: 'top' | 'bottom';
  accentColor?: string;
}

type ItemPositionProps = Pick<ImageTextXSCenteredProps, 'itemPosition'>;

const StyledTitle = styled.h2<{accentColor?: string; size: Size; hasDescription?: boolean}>`
  ${props => (props.size === 'small' ? fontSizesMixinsR4.h2() : fontSizesMixinsR4.h1())}
  margin-left: auto;
  margin-right: auto;
  width: 100%;
  text-align: center;
  margin-top: 0;
  margin-bottom: ${props => (props.hasDescription ? 40 : 140)}px;
  color: ${props => props.accentColor || paletteR4.black.base};
  ${mediaBreakpointDown('tablet')} {
    ${props => (props.size === 'small' ? fontSizesMixinsMobileR4.h2() : fontSizesMixinsMobileR4.h1())}
    margin-bottom: 32px;
  }
`;

export const StyledDescription = styled.p<ItemPositionProps>`
  ${fontSizesMixinsR4.p1()};
  max-width: 720px;
  text-align: center;
  margin: 0 auto ${props => (props.itemPosition === 'top' ? 0 : 80)}px auto;
  color: ${paletteR4.black.base};
  ${mediaBreakpointDown('tablet')} {
    ${fontSizesMixinsMobileR4.p1()};
    margin-bottom: 40px;
  }
`;

const StyledItems = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
`;

interface ItemWrapperProps {
  hasItemsButton: boolean;
  hasImage: boolean;
  itemsPerRow?: number;
  withSliderOnMobile?: boolean;
}

const mobileSlideItemCss = css`
  margin-bottom: 0;
  height: 160px !important;
`;

const StyledItemWrapper = styled.div<ItemWrapperProps>`
  flex-grow: 1;
  flex-shrink: 0;
  flex-basis: ${props => 100 / (props.itemsPerRow || 4)}%;
  padding-left: 12px;
  padding-right: 12px;
  display: flex;
  justify-content: center;
  outline: none;

  ${mediaBreakpointDown('tablet')} {
    flex-basis: ${props => (props.hasItemsButton ? 50 : 25)}%;
    align-items: center;
    margin-bottom: ${props => (props.hasItemsButton ? 80 : 46)}px;
    ${ImageTextBlockDescription} {
      display: ${props => (props.hasItemsButton || props.hasImage ? 'flex' : 'none')};
    }
    ${ImageTextBlockTitle} {
      margin-bottom: ${props => (props.hasItemsButton ? 24 : 0)}px;
    }
  }

  ${mediaBreakpointDown('mobile')} {
    flex-basis: ${props => (props.hasItemsButton ? 100 : 50)}%;
    ${props => props.withSliderOnMobile && mobileSlideItemCss}
  }
`;

const StyledDesktop = styled.div`
  ${hiddenMobileDownStyle}
`;

const StyledMobile = styled.div`
  ${hiddenTabletUpStyle}
`;

const sliderSettings = {
  infinite: false,
  slidesToShow: 1,
  slidesToScroll: 1,
  speed: 500,
  arrows: false,
  dots: true,
  centerMode: true
};

export const ImageTextXSCentered: FC<ImageTextXSCenteredProps> = props => {
  const {items, title, accentColor, imgSize, itemsPerRow, withSliderOnMobile} = props;
  const hasItemsButton = items.find(item => item.buttonText && item.buttonLink) !== undefined;

  const itemsComp = items.map(item => (
    <StyledItemWrapper
      className="xs-centered-item"
      itemsPerRow={itemsPerRow}
      hasItemsButton={hasItemsButton}
      hasImage={Boolean(item.imgSrc)}
      withSliderOnMobile={withSliderOnMobile}
      key={hashObject(item)}
    >
      <ImageTextBlock
        className="xs-centered-item-content"
        {...item}
        itemPosition={props.itemPosition}
        size={props.size}
        type="centered"
        accentColor={accentColor}
        imgSize={imgSize}
      />
    </StyledItemWrapper>
  ));

  const hasDescription = !!props.description;

  const renderItems = () => {
    if (!items.length)
      return null;

    const staticItems = <StyledItems className="xs-centered-items">{itemsComp}</StyledItems>;

    if (withSliderOnMobile)
      return (
        <>
          <StyledDesktop>{staticItems}</StyledDesktop>
          <StyledMobile>
            <Slider className="xs-centered-items" items={itemsComp} settings={sliderSettings} />
          </StyledMobile>
        </>
      );

    return staticItems;
  };

  return (
    <>
      {props.itemPosition === 'top' && renderItems()}
      <StyledTitle accentColor={accentColor} hasDescription={hasDescription} size={props.size}>
        {title}
      </StyledTitle>
      {props.description && (
        <StyledDescription itemPosition={props.itemPosition}>{props.description}</StyledDescription>
      )}
      {props.itemPosition === 'bottom' && renderItems()}
    </>
  );
};
